/**
 * Analytics Events
 * Event tracking functions for all required events
 */

import { EVENT_NAMES } from './constants.js';
import { push_to_data_layer } from './pushToDataLayer.js';
import { detect_device_type } from './device.js';
import { should_block_event, record_event } from './priorities.js';
import { validate_event } from './validators.js';
import { get_page_context } from './pageContext.js';

/**
 * Generic event tracking with payload validation
 * @param {string} event_name - Event name
 * @param {Object} payload - Event payload
 * @returns {boolean} - True if event was pushed
 */
export function track_event(event_name, payload = {}) {
  // Validate event
  if (!validate_event(event_name, payload)) {
    return false;
  }

  // Check priority rules
  if (should_block_event(event_name)) {
    return false;
  }

  // Record this event
  record_event(event_name);

  // Build final payload
  const final_payload = {
    event: event_name,
    ...payload,
    timestamp: Date.now()
  };

  // Push to dataLayer
  return push_to_data_layer(final_payload);
}

/**
 * Track support search event
 * @param {string} query - Search query
 * @param {number} results_count - Number of results
 * @returns {boolean} - True if event was pushed
 */
export function track_support_search(query, results_count = 0) {
  const payload = {
    event: EVENT_NAMES.SUPPORT_SEARCH,
    search: {
      query: String(query || '').trim(),
      results_count: Number(results_count) || 0
    },
    page: {
      type: get_page_context().type
    }
  };

  return push_to_data_layer(payload);
}

/**
 * Track category click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_category_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.CATEGORY_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    category: {
      name: String(event_data.category_name || '').trim(),
      path: String(event_data.category_path || '').trim(),
      level: Number(event_data.category_level) || 1
    },
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.CATEGORY_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.CATEGORY_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track product click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_product_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.PRODUCT_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    product: {
      name: String(event_data.product_name || '').trim(),
      display_name: String(event_data.product_display_name || '').trim(),
      category: String(event_data.product_category || '').trim()
    },
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.PRODUCT_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.PRODUCT_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track file download event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_file_download(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.FILE_DOWNLOAD,
    pdf_name: String(event_data.pdf_name || event_data.file_title || '').trim(),
    clickURL: String(event_data.clickURL || event_data.file_url || '').trim(),
    pagePath: String(event_data.pagePath || (typeof window !== 'undefined' ? window.location.href : '') || '').trim(),
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    category: {
      name: String(event_data.category_name || '').trim()
    },
    product: {
      name: String(event_data.product_name || '').trim()
    },
    file: {
      title: String(event_data.file_title || '').trim(),
      id: String(event_data.file_id || '').trim(),
      language: String(event_data.file_language || '').trim(),
      format: String(event_data.file_format || '').trim(),
      date: String(event_data.file_date || '').trim(),
      type: String(event_data.file_type || '').trim(),
      url: String(event_data.file_url || '').trim()
    },
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (!payload.pdf_name || !payload.clickURL) {
    console.warn('[Analytics] track_file_download missing required fields', {
      pdf_name: payload.pdf_name,
      clickURL: payload.clickURL,
      payload
    });
  }

  if (should_block_event(EVENT_NAMES.FILE_DOWNLOAD)) {
    return false;
  }

  record_event(EVENT_NAMES.FILE_DOWNLOAD);
  return push_to_data_layer(payload);
}

/**
 * Track menu click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_menu_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.MENU_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    navigation: {
      level_1: String(event_data.navigation_level_1 || '').trim(),
      level_2: String(event_data.navigation_level_2 || '').trim(),
      level_3: String(event_data.navigation_level_3 || '').trim(),
      level_4: String(event_data.navigation_level_4 || '').trim(),
      depth: Number(event_data.navigation_depth) || 0
    },
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.MENU_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.MENU_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track CTA click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_cta_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.CTA_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    clicked_name: String(event_data.clicked_name || '').trim(),
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    cta_type: String(event_data.cta_type || '').trim(),
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.CTA_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.CTA_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track hyperlink click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_hyperlink_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.HYPERLINK_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    link_type: String(event_data.link_type || '').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.HYPERLINK_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.HYPERLINK_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track product tab click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_product_tab_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.PRODUCT_TAB_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    clicked_name: String(event_data.clicked_name || '').trim(),
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    product_name: String(event_data.product_name || '').trim(),
    product_category: String(event_data.product_category || '').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.PRODUCT_TAB_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.PRODUCT_TAB_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track filter click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_filter_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.FILTER_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    filter_name: String(event_data.filter_name || '').trim(),
    filter_value: String(event_data.filter_value || '').trim(),
    filter_value_label: String(event_data.filter_value_label || '').trim(),
    previous_value: String(event_data.previous_value || '').trim(),
    results_count: Number(event_data.results_count) || 0,
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.FILTER_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.FILTER_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track slider interaction event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_slider_click(event_data = {}) {
  const payload = {
    event: EVENT_NAMES.SLIDER_CLICK,
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || '').trim(),
    component_variant: String(event_data.component_variant || '').trim(),
    clicked_name: String(event_data.clicked_name || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    slider_name: String(event_data.slider_name || '').trim(),
    from_index: Number(event_data.from_index) || 0,
    to_index: Number(event_data.to_index) || 0,
    total_items: Number(event_data.total_items) || 0,
    interaction_type: String(event_data.interaction_type || 'click').trim(),
    position: Number(event_data.position) || 0,
    device_type: detect_device_type()
  };

  if (should_block_event(EVENT_NAMES.SLIDER_CLICK)) {
    return false;
  }

  record_event(EVENT_NAMES.SLIDER_CLICK);
  return push_to_data_layer(payload);
}

/**
 * Track breadcrumb click event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_breadcrumb_click(event_data = {}) {
  const payload = {
    event: 'breadcrumb_click',
    clicked_area: String(event_data.clicked_area || '').trim(),
    component_name: String(event_data.component_name || 'breadcrumb').trim(),
    breadcrumb: {
      title: String(event_data.breadcrumb_title || '').trim(),
      path: String(event_data.breadcrumb_path || '').trim(),
      level: Number(event_data.breadcrumb_level) || 1,
      position: Number(event_data.breadcrumb_position) || 0
    },
    clicked_text: String(event_data.clicked_text || '').trim(),
    clicked_url: String(event_data.clicked_url || '').trim(),
    breadcrumb_type: String(event_data.breadcrumb_type || 'navigation').trim(),
    device_type: detect_device_type()
  };

  return push_to_data_layer(payload);
}

/**
 * Track homepage view event
 * @param {Object} event_data - Event data
 * @returns {boolean} - True if event was pushed
 */
export function track_homepage_view(event_data = {}) {
  const payload = {
    event: 'homepage_view',
    page: {
      type: get_page_context().type || 'support_home',
      template: String(event_data.page_template || '').trim(),
      category: String(event_data.page_category || 'home').trim()
    },
    section_viewed: String(event_data.section_viewed || '').trim(),
    component_name: String(event_data.component_name || 'homepage').trim(),
    device_type: detect_device_type(),
    timestamp: Date.now()
  };

  return push_to_data_layer(payload);
}
